import { Loader2, type LucideIcon } from "lucide-react"
import type { ComponentType, ReactNode } from "react"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { Button } from "@/components/ui/primitives"
import { cn } from "@/lib/utils"

/**
 * Полка под ассетом шота: слева что это (версии, размер, модель), справа действия с файлом.
 * Под полкой — пульт генерации следующей версии (GenerationBar layout="row").
 */
export function AssetShelf({
  leading,
  meta,
  children,
  className,
}: {
  leading?: ReactNode
  meta?: ReactNode
  children?: ReactNode
  className?: string
}) {
  return (
    <div className={cn("flex min-h-9 w-full min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1", className)}>
      {leading}
      {meta ? <span className="min-w-0 truncate px-1 text-xs text-ui-text-muted tabular-nums">{meta}</span> : null}
      {children ? <div className="ml-auto flex shrink-0 items-center gap-0.5">{children}</div> : null}
    </div>
  )
}

type IconLike = LucideIcon | ComponentType<{ className?: string }>

/** Действие полки: иконка с подсказкой; `text` — когда действию нужна подпись («Обработка»). */
export function ShelfAction({
  label,
  icon: Icon,
  text,
  busy = false,
  disabled = false,
  onClick,
}: {
  label: string
  icon: IconLike
  text?: string
  busy?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  const button = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        "h-8 gap-1.5 text-ui-text-secondary",
        text ? "bg-ui-surface-raised px-2.5 text-xs text-ui-text-primary" : "w-8 px-0",
      )}
      disabled={disabled || busy}
      onClick={onClick}
      aria-label={label}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      {text ? <span>{text}</span> : null}
    </Button>
  )
  if (text) return button
  return <ActionHintTooltip label={label}>{button}</ActionHintTooltip>
}
